import { useEffect, useMemo, useState } from 'react';
import members from '../data/members';
import { useAppContext } from '../context/AppContext';
import copy from '../data/copy';
import assetPath from '../utils/assetPath';
import Seo from '../components/Seo';
import styles from './Videos.module.css';

const FEED_ENDPOINTS = [
  (channelId) => `/api/youtube-feed?channel_id=${channelId}`,
];

const parseLatestVideo = (text) => {
  const xml = new DOMParser().parseFromString(text, 'application/xml');
  if (xml.getElementsByTagName('parsererror').length) {
    return null;
  }
  const entry = xml.getElementsByTagName('entry')[0];
  if (!entry) {
    return null;
  }
  const videoId = entry.getElementsByTagName('yt:videoId')[0]?.textContent;
  const title = entry.getElementsByTagName('title')[0]?.textContent;
  const published = entry.getElementsByTagName('published')[0]?.textContent;
  const link = entry.getElementsByTagName('link')[0]?.getAttribute('href');
  const thumbnail = entry.getElementsByTagName('media:thumbnail')[0]?.getAttribute('url');

  return {
    videoId,
    title,
    published,
    link: link || (videoId ? `https://www.youtube.com/watch?v=${videoId}` : null),
    thumbnail,
  };
};

const formatPublished = (published, language) => {
  if (!published) {
    return null;
  }
  const date = new Date(published);
  if (Number.isNaN(date.getTime())) {
    return null;
  }
  const locale = language === 'fr' ? 'fr-FR' : 'en-US';
  return new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date);
};

const fetchFeed = async (channelId) => {
  let lastError = null;
  for (const buildUrl of FEED_ENDPOINTS) {
    const url = buildUrl(channelId);
    try {
      const response = await fetch(url);
      if (!response.ok) {
        lastError = new Error(`Feed request failed with ${response.status}`);
        continue;
      }
      const text = await response.text();
      return text;
    } catch (error) {
      lastError = error;
    }
  }
  throw lastError || new Error('Failed to load YouTube feed');
};

const Videos = () => {
  const { language } = useAppContext();
  const t = copy[language].videos;
  const youtubers = useMemo(
    () => members.filter((member) => member.youtube && member.youtubeChannelId),
    [],
  );
  const [videos, setVideos] = useState({});
  const [status, setStatus] = useState('loading');
  const [playing, setPlaying] = useState({});

  useEffect(() => {
    let isActive = true;
    const loadVideos = async () => {
      setStatus('loading');
      const results = await Promise.all(
        youtubers.map(async (member) => {
          try {
            const text = await fetchFeed(member.youtubeChannelId);
            const data = parseLatestVideo(text);
            return [member.name, { data, error: null }];
          } catch (error) {
            console.error('Unable to load YouTube feed', member.name, error);
            return [member.name, { data: null, error: error?.message || true }];
          }
        }),
      );

      if (!isActive) {
        return;
      }
      setVideos(Object.fromEntries(results));
      setStatus('ready');
    };

    loadVideos();
    return () => {
      isActive = false;
    };
  }, [youtubers]);

  const isLoading = status === 'loading';

  return (
    <div className={styles.videosView}>
      <Seo
        title={language === 'fr' ? 'Vidéos' : 'Videos'}
        description={t.body}
        image={assetPath('/assets/logo-salty.png')}
        url="https://salty-squad.fr/videos"
      />
      <header className={styles.videosHero}>
        <p className="unit">{t.preheading}</p>
        <h1>{t.title}</h1>
        <p>{t.body}</p>
        {isLoading && <p className={styles.loadingNote}>{t.loading}</p>}
      </header>

      <section className={styles.videosGrid}>
        {youtubers.map((member) => {
          const payload = videos[member.name];
          const video = payload?.data;
          const published = formatPublished(video?.published, language);
          const hasVideo = Boolean(video?.videoId);
          const titleText = video?.title || (isLoading ? t.loading : t.unavailable);
          const previewImage = video?.videoId
            ? `/api/youtube-thumb?video_id=${video.videoId}`
            : video?.thumbnail;
          const isPlaying = Boolean(playing[member.name]);

          return (
            <article key={member.name} className={styles.videoCard}>
              <div className={styles.cardHeader}>
                <div className={styles.memberChip}>
                  <div className={styles.memberAvatar}>
                    {member.image ? (
                      <img src={member.image} alt={`${member.name} portrait`} />
                    ) : (
                      <span aria-hidden="true">{member.name.slice(0, 1)}</span>
                    )}
                  </div>
                  <div className={styles.memberInfo}>
                    <strong>{member.name}</strong>
                    <span>{member.role}</span>
                  </div>
                </div>
                <a href={member.youtube} target="_blank" rel="noreferrer" className={styles.channelLink}>
                  {member.youtubeHandle || 'YouTube'}
                </a>
              </div>

              <div className={styles.videoFrame}>
                {hasVideo ? (
                  isPlaying ? (
                    <iframe
                      src={`https://www.youtube.com/embed/${video.videoId}`}
                      title={`${member.name} latest video`}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      loading="lazy"
                    />
                  ) : (
                    <button
                      type="button"
                      className={styles.videoPreview}
                      onClick={() => setPlaying((prev) => ({ ...prev, [member.name]: true }))}
                    >
                      {previewImage ? (
                        <img src={previewImage} alt="" loading="lazy" />
                      ) : (
                        <span className={styles.previewFallback}>{t.previewUnavailable}</span>
                      )}
                      <span className={styles.playBadge} aria-hidden="true">
                        ▶
                      </span>
                      <span className="sr-only">{t.play}</span>
                    </button>
                  )
                ) : (
                  <div className={styles.videoPlaceholder}>
                    {video?.thumbnail ? (
                      <img src={video.thumbnail} alt="" aria-hidden="true" />
                    ) : null}
                    <p>{t.unavailable}</p>
                  </div>
                )}
              </div>

              <div className={styles.videoMeta}>
                <p className={styles.videoTitle}>{titleText}</p>
                <div className={styles.videoMetaRow}>
                  {published && <span>{published}</span>}
                  <a href={video?.link || member.youtube} target="_blank" rel="noreferrer" className={styles.videoCta}>
                    {t.cta}
                  </a>
                </div>
              </div>
            </article>
          );
        })}
      </section>
    </div>
  );
};

export default Videos;
